import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { ShieldAlert } from "lucide-react";
import { AccessReadiness } from "@/components/auth/AccessReadiness";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import {
  PROJECT_FEATURE_DENIED_ACTION,
  useProjectFeatureAccess,
  type ProjectFeature,
} from "@/hooks/useProjectFeatureAccess";
import { useRbacReadiness } from "@/hooks/useRbacReadiness";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { permissionDeniedMessage } from "@/lib/permissionDenied";

interface ProjectPermissionRouteGuardProps {
  feature: ProjectFeature;
  children: ReactNode;
}

/**
 * Guards a building page a member's per-project permissions can shut them out
 * of (the issue pages, Drawing compare). The sidebar dims those items; this is
 * the same answer for a URL typed or followed directly.
 *
 * Waits until permissions, assignments and the building's projects have
 * loaded, so a permitted member's cold load never flashes the refusal. Denied,
 * the page is not rendered at all — nothing behind the modal fetches — and the
 * modal cannot be dismissed onto it: its one action leaves for the buildings
 * homepage.
 *
 * For the two viewers this is the enforcement: USE_MODEL_VIEWER and
 * VIEW_DRAWING_COMPARE gate the viewer pages, not the data — the chatbot reads
 * IFC/DWG data by file access alone (see useModelViewerGuard).
 */
export const ProjectPermissionRouteGuard = observer(function ProjectPermissionRouteGuard({
  feature,
  children,
}: ProjectPermissionRouteGuardProps) {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const scope = useBuildingScope(id);
  const access = useProjectFeatureAccess(scope);
  const { failed, retry } = useRbacReadiness();

  if (!access.ready) {
    return (
      <AccessReadiness
        failed={failed}
        retry={retry}
        message={t.auth.permissionLoadFailed}
        loadingLabel={t.auth.checkingAccess}
        fullHeight
      />
    );
  }

  if (access.allowed(feature)) return <>{children}</>;

  const copy = t.common.permissions;
  const { title, description } = permissionDeniedMessage(
    copy,
    PROJECT_FEATURE_DENIED_ACTION[feature],
    access.projectId === null ? "anyProject" : "project"
  );

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        hideCloseButton
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" aria-hidden />
            {copy.noAccessTitle}
          </DialogTitle>
          <DialogDescription>
            {title} {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={() => navigate("/buildings")}>{copy.goToBuildings}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
